// @flow
import React, { Component } from 'react'
import axios from 'axios'
import { withRouter } from 'client/apps/login/with-router.js'
import owasp from 'owasp-password-strength-test'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import { PasswordStrengthTooltip } from './password-strength-tooltip'
import routerFunction from 'client/route.js'

const INTERVAL = 50 // 50ms

const EyeIcon = ({ visible }: { visible: boolean }): any => visible
  ? (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
      <circle cx='12' cy='12' r='3' />
    </svg>
    )
  : (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
      <line x1='1' y1='1' x2='23' y2='23' />
    </svg>
    )

owasp.config({
  allowPassphrases: false,
  maxLength: 128,
  minLength: 8,
  minPhraseLength: 20,
  minOptionalTestsToPass: 3
})

type OnInputEventType = {
  target: {
    value: string
  }
}

const defaultPasswordStrength = {
  errors: [],
  failedTests: [],
  strong: false
}
// $FlowFixMe 
export class ChangePasswordForm extends Component {
  props: {
    params: {
      resetToken: string
    },
    router: Object
  }

  // state: {
  //   password: string,
  //   confirmPassword: string
  // }

  state: any = {
    password: '',
    passwordStrength: defaultPasswordStrength,
    confirmPassword: '',
    showPassword: false,
    showConfirmPassword: false
  }
  // $FlowFixMe
  togglePassword: ((key: string) => void) = (key: string) => {
    // $FlowFixMe
    this.setState((prev) => ({ [key]: !prev[key] }))
  }
  // $FlowFixMe 
  onSubmit: ((event: any) => Promise<void>) = async (event: SyntheticEvent) => {
    event.preventDefault()
    const { params: { resetToken } } = this.props
    const router = routerFunction()
    const { password, confirmPassword } = this.state
    if (password === confirmPassword) {
      try {
        await axios.post('/api/v1/forgot-password/reset', { password, resetToken })
        router.navigate('/forgot-password/reset/success')
      } catch (e) {
        router.navigate('/forgot-password/reset/error')
      }
    }
  }

  onInput: ((key: string, OnInputEventType) => void) = (key: string, { target: { value } }: OnInputEventType) => {
    if (key !== 'password') {
      this.setState({ [key]: value })
    } else {
      const passwordStrength = value.length
        ? owasp.test(value)
        : defaultPasswordStrength

      this.setState(// $FlowFixMe
        { password: value, passwordStrength }
      )
    }
  }

  renderPassword: ((showValidationMessage: boolean) => any) = (showValidationMessage: boolean) =>
    <div className='field'>
      <label className='field__label' htmlFor='new-password'>New Password</label>
      <div className='field__password'>
        <input
          id='new-password'
          className='field__input password-input'
          data-tooltip-content
          data-tooltip-id='password-strength'
          data-tooltip-variant='light'
          onInput={this.onInput.bind(this, 'password')}
          placeholder='New password'
          type={this.state.showPassword ? 'text' : 'password'}
        />
        <button
          type='button'
          className='field__eye'
          onClick={() => this.togglePassword('showPassword')}
          aria-label={this.state.showPassword ? 'Hide password' : 'Show password'}
        >
          <EyeIcon visible={this.state.showPassword} />
        </button>
      </div>
      {showValidationMessage
        ? <span className='field__error'>unmet requirements</span>
        : null}
    </div>

  renderTooltip: ((errors: Array<number>, empty: boolean, strong: boolean) => any) = (errors: Array<number>, empty: boolean, strong: boolean) =>
    <ReactTooltip
      className='pw-reset-password__tooltip'
      openOnClick
      float={false}
      render={() => {
        return (
          <PasswordStrengthTooltip
            errors={errors}
            empty={empty}
            strong={strong}
          />
        )
      }}
      id='password-strength'
      place='right'
    />

  renderSubmitButton: ((disabled: boolean) => any) = (disabled: boolean) =>
    <button
      className='login__button'
      disabled={disabled}
      type='submit'
    >
      Submit
    </button>

  render (): any {
    const { password, passwordStrength, confirmPassword } = this.state
    const { failedTests: errors, strong } = passwordStrength
    const passwordInvalid = password.length > 0 && !strong
    const passwordsDontMatch = password.length > 0 &&
      confirmPassword.length > 0 &&
      password !== confirmPassword
    const disableSubmitButton = passwordInvalid ||
      password.length === 0 ||
      confirmPassword.length === 0 ||
      passwordsDontMatch

    return (
      <form className='login__form' onSubmit={this.onSubmit}>
        <div className='validation-warning'>
          <h4>{(passwordsDontMatch)
            ? <span>Both fields must match.</span>
            : null}
          </h4>
        </div>
        {this.renderPassword(passwordInvalid)}
        {this.renderTooltip(errors, password.length === 0, strong)}
        <div className='field'>
          <label className='field__label' htmlFor='confirm-password'>Confirm Password</label>
          <div className='field__password'>
            <input
              id='confirm-password'
              className='field__input confirm-password-input'
              placeholder='Confirm new password'
              type={this.state.showConfirmPassword ? 'text' : 'password'}
              onInput={this.onInput.bind(this, 'confirmPassword')}
            />
            <button
              type='button'
              className='field__eye'
              onClick={() => this.togglePassword('showConfirmPassword')}
              aria-label={this.state.showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon visible={this.state.showConfirmPassword} />
            </button>
          </div>
        </div>
        {this.renderSubmitButton(disableSubmitButton)}
      </form>
    )
  }
}

export default (withRouter(ChangePasswordForm): any)
